import React from 'react';

import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
} from '@companycam/companycam-events';

import DragAndDropUploader from './DragAndDropUploader';
import TASK_TEMPLATE_ASSOCIATE_REFERENCE_PHOTO from './gql/taskTemplateAssociateReferencePhoto.graphql';
import TASK_ASSOCIATE_REFERENCE_PHOTO from './gql/taskAssociateReferencePhoto.graphql';

import toast from 'helpers/toast';

import { IMAGE_MAX_SIZE_BYTES } from 'constants/files';

import { maxReferencePhotoUploadCount, referencePhotosView } from './constants';
import useDirectUpload from 'javascripts/hooks/useDirectUpload';
import { uploadFile } from 'helpers/direct-upload';

import { translate } from 'translations/i18n';

const ReferencePhotosUploader = ({
  assetCount,
  isTemplate,
  listId,
  projectId,
  refetchHeaderInfo,
  setSelectedView,
  taskId,
}) => {
  const { createDirectUpload } = useDirectUpload();
  const [taskTemplateAssociateReferencePhoto] = useMutation(
    TASK_TEMPLATE_ASSOCIATE_REFERENCE_PHOTO,
  );
  const [taskAssociateReferencePhoto] = useMutation(
    TASK_ASSOCIATE_REFERENCE_PHOTO,
  );
  const onFinished = async (files) => {
    if (!files.length) {
      return;
    }
    await refetchHeaderInfo({
      variables: { id: listId },
    });
    toast.success(
      translate('projects.taskList.referencePhotoUploadModal.success', {
        count: files.length,
      }),
    );
    setSelectedView(referencePhotosView.PREVIEW);
  };

  const associateReferencePhoto = async (id, signedBlobId) => {
    if (isTemplate) {
      await taskTemplateAssociateReferencePhoto({
        variables: {
          input: {
            taskTemplateId: id,
            signedBlobId,
          },
        },
      });
    } else {
      await taskAssociateReferencePhoto({
        variables: {
          input: {
            taskId: id,
            signedBlobId,
          },
        },
      });
    }
  };

  const uploader = async (file) => {
    try {
      const { data } = await createDirectUpload({ file });
      const {
        createDirectUpload: {
          directUpload: { url, headers, signedBlobId },
        },
      } = data;
      await uploadFile(url, headers, file);
      await associateReferencePhoto(taskId, signedBlobId);

      const eventName = isTemplate
        ? EventNames.TASK_LIST_TEMPLATES.ADD_REFERENCE_IMAGE
        : EventNames.TASK_LISTS.ADD_REFERENCE_IMAGE;

      CCEvents.logEvent(eventName, {
        [EventPropertyKeys.PROJECT_ID]: projectId,
        [EventPropertyKeys.TASK_LIST_ID]: listId,
      });
      return true;
    } catch (err) {
      toast.error(
        translate('projects.taskList.referencePhotoUploadModal.error', {
          fileName: file.name,
        }),
      );
      return false;
    }
  };

  return (
    <DragAndDropUploader
      uploader={uploader}
      maxFileSizeBytes={IMAGE_MAX_SIZE_BYTES}
      onFinishedCallback={onFinished}
      totalUploadedFiles={assetCount}
      maxReferencePhotoUploadCount={maxReferencePhotoUploadCount}
    />
  );
};

ReferencePhotosUploader.propTypes = {
  assetCount: PropTypes.number.isRequired,
  isTemplate: PropTypes.bool.isRequired,
  listId: PropTypes.string,
  projectId: PropTypes.string,
  taskId: PropTypes.string.isRequired,
  refetchHeaderInfo: PropTypes.func.isRequired,
  setSelectedView: PropTypes.func.isRequired,
};

export default ReferencePhotosUploader;
